import {
  CheckCircle2,
  Circle,
  Anchor,
  Container,
  Ship,
  PackageCheck,
  Truck,
  MapPin,
  ChevronRight,
} from "lucide-react";

const MILESTONES = [
  { id: 1, label: "Booking Confirmed", icon: CheckCircle2, state: "done" },
  { id: 2, label: "Container Pick-up", icon: Container, state: "done" },
  { id: 3, label: "Port Gate-In", icon: Anchor, state: "active" },
  { id: 4, label: "Loaded", icon: PackageCheck, state: "pending" },
  { id: 5, label: "At Sea", icon: Ship, state: "pending" },
  { id: 6, label: "Destination Port", icon: MapPin, state: "pending" },
  { id: 7, label: "Delivered", icon: Truck, state: "pending" },
];

const STAT = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-400">
      {label}
    </div>
    <div
      className={`mt-1.5 text-[#0B1F3A] text-sm font-semibold ${
        mono ? "font-mono" : ""
      }`}
    >
      {value}
    </div>
  </div>
);

export default function DashboardMockup() {
  return (
    <div
      data-testid="hero-dashboard-mockup"
      className="relative mx-auto w-full max-w-6xl rounded-2xl border border-slate-200 bg-white shadow-[0_30px_80px_-30px_rgba(11,31,58,0.25)] overflow-hidden"
    >
      {/* Browser-like chrome */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50/80">
        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
        <div className="ml-3 flex-1 h-6 rounded-md bg-white border border-slate-200 px-2.5 flex items-center font-mono text-[11px] text-slate-400">
          workspace.demaxtore.com/freightiq/shipments/SH-2481
        </div>
        <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-mono text-slate-400">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
          LIVE
        </span>
      </div>

      {/* App shell */}
      <div className="grid grid-cols-12 min-h-[460px]">
        {/* Sidebar */}
        <aside className="hidden md:flex md:col-span-3 lg:col-span-2 flex-col border-r border-slate-100 bg-white p-4">
          <div className="flex items-center gap-2 px-2 py-1.5">
            <span className="h-7 w-7 rounded-md bg-[#0B1F3A] text-white flex items-center justify-center">
              <Ship className="h-3.5 w-3.5" />
            </span>
            <span className="font-display font-bold text-sm tracking-tight">
              FreightIQ™
            </span>
          </div>
          <nav className="mt-6 space-y-1 text-sm">
            {[
              { l: "Bookings", active: true },
              { l: "Containers" },
              { l: "Carriers" },
              { l: "Documents" },
              { l: "Milestones" },
            ].map((i) => (
              <div
                key={i.l}
                className={`px-2.5 py-2 rounded-lg flex items-center gap-2 ${
                  i.active
                    ? "bg-[#3B82F6]/8 text-[#0B1F3A] font-semibold"
                    : "text-slate-500 font-medium hover:bg-slate-50"
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    i.active ? "bg-[#3B82F6]" : "bg-slate-300"
                  }`}
                />
                {i.l}
              </div>
            ))}
          </nav>
          <div className="mt-auto pt-6 text-[10px] tracking-[0.18em] uppercase font-bold text-slate-400">
            Workspace
          </div>
          <div className="mt-2 text-xs text-slate-500">Demir Trading Co.</div>
        </aside>

        {/* Main */}
        <div className="col-span-12 md:col-span-9 lg:col-span-10 p-5 md:p-7 bg-slate-50/40">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-400 flex items-center gap-1.5">
                Shipments
                <ChevronRight className="h-3 w-3" />
                <span className="text-slate-500">Active</span>
              </div>
              <div className="mt-1.5 flex items-center gap-3">
                <h3 className="font-display font-extrabold text-2xl md:text-3xl text-[#0B1F3A] tracking-tight">
                  <span className="font-mono text-[#0B1F3A]">SH-2481</span>
                </h3>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 text-[11px] font-semibold">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Confirmed
                </span>
              </div>
              <div className="mt-1 text-sm text-slate-500">
                Mersin Port{" "}
                <span className="font-mono text-slate-400">TRMER</span>
                <span className="mx-1.5 text-slate-300">→</span>
                Mombasa <span className="font-mono text-slate-400">KEMBA</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="hidden md:inline-flex items-center px-2.5 py-1 rounded-md border border-slate-200 bg-white text-[11px] font-mono text-slate-500">
                BL · DM-T-2481
              </span>
              <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-[#0B1F3A] text-white text-[11px] font-semibold">
                FCL · 40HQ
              </span>
            </div>
          </div>

          {/* Stat row */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-5 md:gap-6 rounded-xl border border-slate-100 bg-white p-5">
            <STAT label="Carrier" value="MSC" />
            <STAT label="Container" value="40HQ · MSCU-7728190" mono />
            <STAT label="ETD" value="12 Jun 2026" />
            <STAT label="ETA" value="28 Jun 2026" />
          </div>

          {/* Timeline */}
          <div className="mt-7">
            <div className="flex items-center justify-between">
              <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-400">
                Milestone Progress
              </div>
              <div className="text-[11px] font-mono text-slate-400">
                3 / 7 · 43%
              </div>
            </div>

            {/* Horizontal stepper */}
            <div className="mt-4 overflow-x-auto -mx-2 px-2 pb-2">
              <div className="flex items-start gap-2 min-w-[640px]">
                {MILESTONES.map((m, idx) => {
                  const Icon = m.icon;
                  const isDone = m.state === "done";
                  const isActive = m.state === "active";
                  return (
                    <div
                      key={m.id}
                      className="flex-1 flex flex-col items-center text-center"
                    >
                      <div className="relative w-full flex items-center">
                        <div
                          className={`flex-1 h-px ${
                            idx === 0
                              ? "opacity-0"
                              : isDone || isActive
                                ? "bg-[#3B82F6]"
                                : "bg-slate-200"
                          }`}
                        />
                        <div
                          className={`relative z-10 flex items-center justify-center h-9 w-9 rounded-full shrink-0 transition-colors ${
                            isDone
                              ? "bg-[#3B82F6] text-white shadow-[0_0_0_4px_rgba(59,130,246,0.12)]"
                              : isActive
                                ? "bg-white border-2 border-[#3B82F6] text-[#3B82F6] shadow-[0_0_0_4px_rgba(59,130,246,0.12)]"
                                : "bg-white border border-slate-200 text-slate-300"
                          }`}
                        >
                          {isDone ? (
                            <CheckCircle2 className="h-4 w-4" />
                          ) : isActive ? (
                            <span className="relative flex h-2.5 w-2.5">
                              <span className="absolute inline-flex h-full w-full rounded-full bg-[#3B82F6] opacity-60 dmx-pulse-dot" />
                              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#3B82F6]" />
                            </span>
                          ) : (
                            <Icon className="h-4 w-4" />
                          )}
                        </div>
                        <div
                          className={`flex-1 h-px ${
                            idx === MILESTONES.length - 1
                              ? "opacity-0"
                              : isDone
                                ? "bg-[#3B82F6]"
                                : "bg-slate-200"
                          }`}
                        />
                      </div>
                      <div
                        className={`mt-2.5 text-[11px] leading-tight font-semibold ${
                          isDone || isActive
                            ? "text-[#0B1F3A]"
                            : "text-slate-400"
                        }`}
                      >
                        {m.label}
                      </div>
                      <div className="mt-1 text-[10px] font-mono text-slate-400">
                        {isDone ? "Done" : isActive ? "In progress" : "—"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Live event feed strip */}
          <div className="mt-6 rounded-xl border border-slate-100 bg-white p-4 flex items-center gap-3">
            <span className="h-2 w-2 rounded-full bg-[#3B82F6] dmx-pulse-dot" />
            <div className="text-sm">
              <span className="font-semibold text-[#0B1F3A]">
                Port Gate-In confirmed
              </span>
              <span className="text-slate-500">
                {" "}
                · Mersin Port · 09:42 GMT+3 · MSC vessel allocation locked
              </span>
            </div>
            <span className="ml-auto hidden sm:inline-block text-[10px] font-mono text-slate-400">
              EVT-#10293
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
